/**
 * ProfilePage — Account & Profile Management
 *
 * Two sections:
 *  - My Profile: view/edit name and phone
 *  - Change Password: current → new → confirm with strength meter
 */
import React, { useState, useEffect } from "react";
import { api } from "../services/api.js";
import { setTokens } from "../services/auth.js";

// ─── Styles ───────────────────────────────────────────────────────────────────
const lbl: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.05em", color: "var(--muted)", display: "block", marginBottom: 4,
};
const input: React.CSSProperties = {
  width: "100%", padding: "10px 12px", border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)", background: "var(--bg)", color: "var(--ink)",
  fontSize: 14, fontFamily: "inherit", boxSizing: "border-box",
};
const card: React.CSSProperties = {
  background: "var(--panel)", border: "1px solid var(--border)",
  borderRadius: "var(--radius)", padding: "24px 28px", boxShadow: "var(--shadow)", marginBottom: 20,
};

function PasswordStrengthBar({ password }: { password: string }): React.ReactElement {
  function score(p: string): number {
    let s = 0;
    if (p.length >= 8) s++;
    if (p.length >= 12) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  }
  const s = password ? score(password) : 0;
  const colors = ["", "#ef4444", "#f59e0b", "#f59e0b", "#10b981", "#10b981"];
  const labels = ["", "Weak", "Fair", "Good", "Strong", "Very Strong"];
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", gap: 4, height: 4 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} style={{ flex: 1, borderRadius: 999, background: n <= s ? colors[s] : "var(--border)", transition: "background 0.2s" }} />
        ))}
      </div>
      {password && (
        <p style={{ margin: "4px 0 0", fontSize: 11, color: colors[s] ?? "var(--muted)" }}>{labels[s]}</p>
      )}
    </div>
  );
}

interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: string;
  institution_id?: string;
  phone?: string;
  is_active: boolean;
  created_at: string;
}

export function ProfilePage(): React.ReactElement {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Profile form state
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  // Password form state
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);
  const [pwdMsg, setPwdMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    void api.auth.me()
      .then((r) => {
        setProfile(r.data as UserProfile);
        setEditName(r.data.name ?? "");
        setEditPhone((r.data as { phone?: string }).phone ?? "");
      })
      .finally(() => setLoadingProfile(false));
  }, []);

  async function saveProfile(): Promise<void> {
    if (!editName.trim()) { setProfileMsg({ type: "err", text: "Name cannot be empty" }); return; }
    setSaving(true);
    setProfileMsg(null);
    try {
      const res = await api.auth.updateMe({ name: editName.trim(), phone: editPhone.trim() || undefined });
      setProfile((prev) => prev ? { ...prev, ...res.data } : null);
      setProfileMsg({ type: "ok", text: "Profile updated successfully." });
    } catch (e: unknown) {
      setProfileMsg({ type: "err", text: e instanceof Error ? e.message : "Update failed" });
    } finally {
      setSaving(false);
    }
  }

  async function changePassword(): Promise<void> {
    if (!currentPwd || !newPwd || !confirmPwd) {
      setPwdMsg({ type: "err", text: "All password fields are required." }); return;
    }
    if (newPwd.length < 8) {
      setPwdMsg({ type: "err", text: "New password must be at least 8 characters." }); return;
    }
    if (newPwd !== confirmPwd) {
      setPwdMsg({ type: "err", text: "New passwords do not match." }); return;
    }
    setChangingPwd(true);
    setPwdMsg(null);
    try {
      const res = await api.auth.changePassword(currentPwd, newPwd);
      // Store new tokens so the session stays live
      setTokens(res.data.access_token, res.data.refresh_token);
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
      setPwdMsg({ type: "ok", text: "Password changed successfully. Your session has been refreshed." });
    } catch (e: unknown) {
      setPwdMsg({ type: "err", text: e instanceof Error ? e.message : "Password change failed" });
    } finally {
      setChangingPwd(false);
    }
  }

  if (loadingProfile) {
    return <p style={{ color: "var(--muted)", fontSize: 14 }}>Loading profile…</p>;
  }

  const roleLabel = (profile?.role ?? "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const joinedDate = profile?.created_at ? new Date(profile.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—";

  return (
    <>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: "var(--ink)" }}>My Account</h1>
        <p style={{ margin: "4px 0 0", color: "var(--muted)", fontSize: 14 }}>Manage your profile and security settings.</p>
      </div>

      {/* ─── Profile Card ─────────────────────────────────────── */}
      <div style={card}>
        {/* Avatar + identity */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: "var(--brand)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 22, fontWeight: 900, flexShrink: 0,
          }}>
            {(profile?.name?.[0] ?? "?").toUpperCase()}
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "var(--ink)" }}>{profile?.name ?? "—"}</p>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--muted)" }}>{profile?.email}</p>
          </div>
          <div style={{ marginLeft: "auto", textAlign: "right" }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", padding: "4px 10px", borderRadius: 999, background: "var(--brand)18", color: "var(--brand)", border: "1px solid var(--brand)40" }}>
              {roleLabel}
            </span>
            <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--muted)" }}>Member since {joinedDate}</p>
          </div>
        </div>

        <hr style={{ border: "none", borderTop: "1px solid var(--border)", marginBottom: 20 }} />
        <h2 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink)" }}>Edit Profile</h2>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <label htmlFor="profile-name" style={lbl}>Full Name</label>
            <input id="profile-name" type="text" value={editName} onChange={(e) => setEditName(e.target.value)} style={input} />
          </div>
          <div>
            <label htmlFor="profile-phone" style={lbl}>Phone</label>
            <input id="profile-phone" type="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="+251 91 000 0000" style={input} />
          </div>
          <div>
            <label style={lbl}>Email</label>
            <input type="email" value={profile?.email ?? ""} readOnly style={{ ...input, color: "var(--muted)", cursor: "not-allowed" }} />
          </div>
          <div>
            <label style={lbl}>Role</label>
            <input type="text" value={roleLabel} readOnly style={{ ...input, color: "var(--muted)", cursor: "not-allowed" }} />
          </div>
        </div>

        {profileMsg && (
          <div role="alert" style={{ fontSize: 13, padding: "10px 14px", borderRadius: "var(--radius-sm)", marginBottom: 12, background: profileMsg.type === "ok" ? "#f0fdf4" : "var(--danger-bg)", color: profileMsg.type === "ok" ? "#166534" : "var(--danger)", border: `1px solid ${profileMsg.type === "ok" ? "#bbf7d0" : "var(--danger)"}` }}>
            {profileMsg.text}
          </div>
        )}

        <button onClick={() => void saveProfile()} disabled={saving}
          style={{ padding: "10px 24px", borderRadius: "var(--radius-sm)", border: "none", background: saving ? "var(--muted)" : "var(--brand)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer" }}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {/* ─── Change Password Card ──────────────────────────────── */}
      <div style={card}>
        <h2 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink)" }}>Change Password</h2>
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "var(--muted)" }}>After changing your password all other active sessions will be invalidated.</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14, maxWidth: 400 }}>
          <div>
            <label htmlFor="pwd-current" style={lbl}>Current Password</label>
            <div style={{ position: "relative" }}>
              <input id="pwd-current" type={showCurrent ? "text" : "password"} value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} style={{ ...input, paddingRight: 40 }} />
              <button onClick={() => setShowCurrent(!showCurrent)} type="button"
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "var(--muted)" }}>
                {showCurrent ? "🙈" : "👁️"}
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="pwd-new" style={lbl}>New Password</label>
            <div style={{ position: "relative" }}>
              <input id="pwd-new" type={showNew ? "text" : "password"} value={newPwd} onChange={(e) => setNewPwd(e.target.value)} style={{ ...input, paddingRight: 40 }} />
              <button onClick={() => setShowNew(!showNew)} type="button"
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "var(--muted)" }}>
                {showNew ? "🙈" : "👁️"}
              </button>
            </div>
            <PasswordStrengthBar password={newPwd} />
          </div>
          <div>
            <label htmlFor="pwd-confirm" style={lbl}>Confirm New Password</label>
            <input id="pwd-confirm" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} style={{ ...input, borderColor: confirmPwd && confirmPwd !== newPwd ? "var(--danger)" : "var(--border)" }} />
            {confirmPwd && confirmPwd !== newPwd && (
              <p style={{ fontSize: 11, color: "var(--danger)", margin: "4px 0 0" }}>Passwords do not match.</p>
            )}
          </div>
        </div>

        {pwdMsg && (
          <div role="alert" style={{ fontSize: 13, padding: "10px 14px", borderRadius: "var(--radius-sm)", margin: "14px 0 0", maxWidth: 400, background: pwdMsg.type === "ok" ? "#f0fdf4" : "var(--danger-bg)", color: pwdMsg.type === "ok" ? "#166534" : "var(--danger)", border: `1px solid ${pwdMsg.type === "ok" ? "#bbf7d0" : "var(--danger)"}` }}>
            {pwdMsg.text}
          </div>
        )}

        <button onClick={() => void changePassword()} disabled={changingPwd || !currentPwd || !newPwd || !confirmPwd}
          style={{ marginTop: 16, padding: "10px 24px", borderRadius: "var(--radius-sm)", border: "none", background: changingPwd || !currentPwd || !newPwd || !confirmPwd ? "var(--muted)" : "#dc2626", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          {changingPwd ? "Changing…" : "Change Password"}
        </button>
      </div>
    </>
  );
}
